import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import LocationChart from './LocationChart';
import {
  STATUS_THEME,
  dashboardStatusLine,
  floodOutlookLine,
  formatAsOf,
  levelStatus,
  nextSixHourRainMm,
  sensorPlace,
} from './levelStatus';
import { useSensorSeries } from './useSensorSeries';
import { UNIT_LABELS, UNIT_SYSTEMS, formatRain, formatWater } from './units';
import {
  maxMm,
  sumMm,
  toDateInputValue,
  validateCustomRange,
  VIEW_ORDER,
  VIEWS,
} from './waterLevelQuery';

// Chart shading: one flood forecast's history at a time.
const ALERT_SOURCES = [
  { id: 'rule', label: 'Rain rule' },
  { id: 'rf', label: 'AI model' },
  { id: 'none', label: 'None' },
];

const defaultCustomRange = () => {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - 14);
  return { from: toDateInputValue(from), to: toDateInputValue(to) };
};

const LocationPage = ({ sensors, unitSystem, onUnitSystemChange }) => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const knownSensor = sensors.find((sensor) => sensor.id === deviceId);
  const [view, setView] = useState('day');
  const [customDraft, setCustomDraft] = useState(defaultCustomRange);
  const [customApplied, setCustomApplied] = useState(null);
  const [unitsOpen, setUnitsOpen] = useState(false);
  const [alertSource, setAlertSource] = useState('rule');
  const customError = validateCustomRange(customDraft, new Date());
  const { sensor, status, series, ready, error, loading } = useSensorSeries(
    deviceId,
    view,
    view === 'custom' ? customApplied : null,
    knownSensor,
    alertSource
  );
  const shown = sensor || knownSensor;
  const place = sensorPlace(shown, deviceId);
  const yellowMm = shown?.yellowMm;
  const redMm = shown?.redMm;
  const reading = status.latest;
  const nowMs = series?.range?.nowMs || Date.now();
  const levelMm = reading?.levelMm ?? null;
  const atMs = reading?.atMs ?? null;
  const statusId = levelStatus(levelMm, yellowMm, redMm, atMs, nowMs);
  const theme = STATUS_THEME[statusId] || STATUS_THEME.unknown;
  const outlook = floodOutlookLine(status.rfAlert, nowMs);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') navigate('/');
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate]);

  const selectView = (next) => {
    setView(next);
    if (next === 'custom' && !customApplied && !customError) setCustomApplied({ ...customDraft });
  };

  const applyCustomRange = () => {
    if (customError) return;
    setCustomApplied({ ...customDraft });
    setView('custom');
  };

  const stats = useMemo(() => {
    const water = (series?.waterBuckets || []).filter((point) => point.mm != null && point.t <= nowMs);
    return {
      max: maxMm(water),
      avg: water.length ? sumMm(water) / water.length : null,
    };
  }, [series, nowMs]);

  const rainNext = nextSixHourRainMm(series, status.alert);
  const chartEnd = series ? series.range.endMs : nowMs;
  const waitingForRange = view === 'custom' && !customApplied;
  const hasWater = (series?.waterBuckets || []).some((point) => point.mm != null);

  return (
    <main className="page location-page">
      <div className="location-tools">
        <div className="unit-settings">
          <button
            type="button"
            className="text-button"
            aria-expanded={unitsOpen}
            aria-controls="unit-menu"
            onClick={() => setUnitsOpen((open) => !open)}
          >
            Units
          </button>
          {unitsOpen && (
            <div className="unit-menu" id="unit-menu" role="group" aria-label="Units">
              {UNIT_SYSTEMS.map((system) => (
                <button
                  key={system}
                  type="button"
                  aria-pressed={unitSystem === system}
                  onClick={() => onUnitSystemChange(system)}
                >
                  {UNIT_LABELS[system]}
                </button>
              ))}
            </div>
          )}
        </div>
        <button type="button" className="icon-button" aria-label="Close" onClick={() => navigate('/')}>
          ×
        </button>
      </div>

      <h1>{place.title}</h1>
      <p className="status-line" style={{ background: theme.tint }}>
        {loading && levelMm == null
          ? 'Loading this sensor…'
          : dashboardStatusLine({ status: statusId, levelMm, yellowMm, redMm, unitSystem })}
        <span className="status-as-of">
          {loading && atMs == null ? 'Checking the latest reading…' : formatAsOf(atMs)}
        </span>
        {outlook && (
          <span className="status-outlook" role="status" style={{ background: STATUS_THEME.flooding.marker }}>
            {outlook}
          </span>
        )}
      </p>
      <p className="location-alert">
        <Link className="btn btn-primary" to={`/alerts?sensor=${encodeURIComponent(deviceId)}`}>
          Get alerts
        </Link>
      </p>
      <section className="graph-card" aria-label="Water level">
        <div className="graph-toolbar">
          <div className="segmented" role="group" aria-label="Time range">
            {VIEW_ORDER.map((key) => (
              <button
                key={key}
                type="button"
                aria-pressed={view === key}
                onClick={() => selectView(key)}
              >
                {VIEWS[key].label}
              </button>
            ))}
          </div>
          <div className="chart-layers">
            <span className="chart-layers-label" id="flood-forecast-label">Flood forecast</span>
            <div className="segmented" role="group" aria-labelledby="flood-forecast-label">
              {ALERT_SOURCES.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={alertSource === id}
                  onClick={() => setAlertSource(id)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {view === 'custom' && (
            <div className="custom-range">
              <label>
                From
                <input
                  type="date"
                  value={customDraft.from}
                  max={customDraft.to || toDateInputValue(new Date())}
                  onChange={(event) => setCustomDraft((draft) => ({ ...draft, from: event.target.value }))}
                />
              </label>
              <label>
                To
                <input
                  type="date"
                  value={customDraft.to}
                  min={customDraft.from}
                  max={toDateInputValue(new Date())}
                  onChange={(event) => setCustomDraft((draft) => ({ ...draft, to: event.target.value }))}
                />
              </label>
              <button
                type="button"
                className="btn btn-primary"
                onClick={applyCustomRange}
                disabled={!!customError}
              >
                Apply range
              </button>
              {customError && <span className="custom-range-error">{customError}</span>}
            </div>
          )}
        </div>

        {waitingForRange ? (
          <div className="chart-plot chart-plot-water">
            <p className="chart-empty">Pick a valid From and To date, then Apply range.</p>
          </div>
        ) : loading && !ready ? (
          <div className="chart-plot chart-plot-water">
            <p className="chart-empty">Loading this sensor…</p>
          </div>
        ) : error ? (
          <p className="banner-error" role="alert">This sensor’s history could not be loaded.</p>
        ) : hasWater && series ? (
          <LocationChart
            water={series.waterBuckets}
            bands={series.bands}
            start={series.range.startMs}
            end={chartEnd}
            timeUnit={series.range.timeUnit}
            yellowMm={yellowMm}
            redMm={redMm}
            unitSystem={unitSystem}
            alertSource={alertSource}
          />
        ) : (
          <div className="chart-plot chart-plot-water">
            <p className="chart-empty">Not available yet</p>
          </div>
        )}
      </section>

      <dl className="stat-row stat-row-3">
        <div>
          <dt>Highest (period)</dt>
          <dd className="num">{stats.max == null ? 'Not available yet' : formatWater(stats.max, unitSystem)}</dd>
        </div>
        <div>
          <dt>Average (period)</dt>
          <dd className="num">{stats.avg == null ? 'Not available yet' : formatWater(stats.avg, unitSystem)}</dd>
        </div>
        <div>
          <dt>Forecast rain next 6 h</dt>
          <dd className="num">{rainNext == null ? 'Not available yet' : formatRain(rainNext, unitSystem)}</dd>
        </div>
      </dl>
    </main>
  );
};

export default LocationPage;
